import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PaymentIntent } from '@app/interfaces/payments/PaymentIntent.type.interface';
import { PaymentIntentOfferPriceIncrease } from '@app/interfaces/payments/PaymentIntentOfferPriceIncrease.type.interface';
import { UserAccountDataStripe } from '@app/shared/interfaces/stripe/stripe.account.interface';
import { BankAccountStripe } from '@app/shared/interfaces/payment/BankAccount.type.interface';

export interface PaymentIntentData {
  offerId: number;
  paymentMethodId: string;
  idempotencyKey: string;
  confirmed: false;
  couponCode: undefined;
}

@Injectable()
export class PaymentService {
  baseHref = `${environment.apiUrl}/payments`;

  constructor(private http: HttpClient) {}

  createSetupIntent(data: {
    customer: string;
    usage: 'on_session';
  }): Observable<any> {
    const url = `${this.baseHref}/setup-intent`;
    return this.http.post<any>(url, data);
  }

  createStripeAccount(accountData: UserAccountDataStripe): Observable<any> {
    const url = `${this.baseHref}/account/create`;
    return this.http.post<any>(url, accountData);
  }

  createCheckoutSession(data: any): Observable<any> {
    const url = `${this.baseHref}/create-checkout-session`;
    return this.http.post(url, data);
  }

  getStripeCustomer(): Observable<any> {
    const url = `${this.baseHref}/customers`;
    return this.http.get<any>(url);
  }

  getStripeAccount(): Observable<any> {
    const url = `${this.baseHref}/accounts`;
    return this.http.get<any>(url);
  }

  addExternalBankAccount(data: BankAccountStripe): Observable<any> {
    const url = `${this.baseHref}/accounts/bank_account`;
    return this.http.post<any>(url, data);
  }

  getPaymentMethods(): Observable<any> {
    const url = `${this.baseHref}/payment-method`;
    return this.http.get<any>(url);
  }

  attachPaymentMethod(data: any): Observable<any> {
    const url = `${this.baseHref}/payment-method`;
    return this.http.post<any>(url, data);
  }

  detachPaymentMethod(id: string): Observable<any> {
    if (!id) {
      throw new Error('Invalid paymentMethod id provided');
    }
    const url = `${this.baseHref}/payment-method/detach/${id}`;
    return this.http.get<any>(url);
  }

  /**
   * Create a Payment Intent.
   * useStoredCredits - Stored Credits are enabled by default
   */
  createPaymentIntent(
    data: PaymentIntent,
    isMobilePay: boolean,
    isApplePay?: boolean,
  ): Observable<any> {
    let url = `${this.baseHref}/payment-intent`;

    const queryParams = [];
    if (isMobilePay) {
      queryParams.push('mobilepay=true');
    }
    if (isApplePay) {
      queryParams.push('applepay=true');
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    // include google pay
    return this.http.post<any>(url, data);
  }

  createPaymentIntentPriceIncrease(
    data: PaymentIntentOfferPriceIncrease,
    isMobilePay: boolean,
  ): Observable<any> {
    let url;
    if (isMobilePay) {
      url = `${this.baseHref}/payment-intent/offer-increase?mobilepay=true`;
    } else {
      url = `${this.baseHref}/payment-intent/offer-increase`;
    }
    return this.http.post<any>(url, data);
  }

  /**
   * Verify User with Account Links
   * * */
  verifyUser(): Observable<any> {
    const url = `${this.baseHref}/account-link`;
    return this.http.get<any>(url);
  }

  /**
   *  Get user invocies
   *  @param data object - customerId
   * */
  charges(options?: {
    start?: Date;
    end?: Date;
    last?: string;
  }): Observable<any> {
    const url = `${this.baseHref}/charges`;
    const finalOptions: any =
      options?.start || options?.end || options?.last
        ? { params: {} }
        : undefined;
    if (options?.start) {
      finalOptions.params.start = options.start.valueOf() / 1000;
    }
    if (options?.end) {
      finalOptions.params.end = options.end.valueOf() / 1000;
    }
    if (options?.last) {
      finalOptions.params.last = options.last;
    }
    return this.http.get<any>(url, finalOptions);
  }

  getChargesByOfferId(offerId: number): Observable<any> {
    const url = `${this.baseHref}/charges/offer/${offerId}`;
    return this.http.get<any>(url);
  }

  listPayouts(options?: {
    start?: Date;
    end?: Date;
    last?: string;
  }): Observable<any> {
    const url = `${this.baseHref}/list-payouts`;
    const finalOptions: any =
      options?.start || options?.end || options?.last
        ? { params: {} }
        : undefined;
    if (options?.start) {
      finalOptions.params.start = Math.floor(options.start.valueOf() / 1000);
    }
    if (options?.end) {
      finalOptions.params.end = Math.floor(options.end.valueOf() / 1000);
    }
    if (options?.last) {
      finalOptions.params.last = options.last;
    }
    return this.http.get(url, finalOptions);
  }

  testCoupon(data: { couponCode: string; offerId: number }): Observable<any> {
    const url = `${this.baseHref}/test-coupon-code`;
    return this.http.post<any>(url, data);
  }
}
